import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { Dispatch, SetStateAction, useState } from 'react';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Customer, ReceiptItem } from '../models';
import EditIcon from '@mui/icons-material/Edit';
import { dollarFormatter } from '../utils';

const CollectItems: React.FC<{
  items: ReceiptItem[];
  setItems: Dispatch<SetStateAction<ReceiptItem[]>>;
  customers: Customer[];
}> = ({ items, setItems, customers }) => {
  const theme = useTheme();
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [newItemInput, setNewItemInput] = useState<ReceiptItem>({
    name: '',
    price: '',
    customers: [],
  });

  const [modifyItemDialogOpen, setModifyItemDialogOpen] = useState(false);
  const [modifiedItemIndex, setModifiedItemIndex] = useState(0);
  const [modifiedItemInput, setModifiedItemInput] = useState<ReceiptItem>({
    name: '',
    price: '',
    customers: [],
  });
  const [deleteItemDialogOpen, setDeleteItemDialogOpen] = useState(false);
  const getNonEmptyCustomers = (currentCustomers: Customer[]) => {
    return currentCustomers.filter(
      (customer) => customer.name.trim().length !== 0,
    );
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'end',
        width: '100%',
        overflow: 'hidden',
        gap: '8px'
      }}
    >
      {/* displays item contents */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'end',
          width: '100%',
          padding: '8px 0',
          overflow: 'hidden',
        }}
      >
        {items.map((item, index) => {
          return (
            <Grid
              sx={{
                padding: '16px 8px',
                backgroundColor:
                  index % 2 === 0 ? theme.palette.primary.light : 'none',
              }}
              key={index}
              container
            >
              {/* displays item content*/}
              <Grid
                item
                xs={11}
              >
                <Button
                  fullWidth
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                    gap: '16px',
                  }}
                  onClick={() => {
                    setModifyItemDialogOpen(true);
                    setModifiedItemInput(item);
                    setModifiedItemIndex(index);
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: '16px',
                    }}
                  >
                    <Chip label={`${index + 1}`} />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Typography
                        variant='body1'
                        sx={{
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          maxWidth: '7rem',
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography
                        variant='body1'
                        sx={{ display: 'flex', alignItems: 'end' }}
                      >
                        {`$${item.price}`}
                      </Typography>
                    </Box>
                    <EditIcon fontSize='small' />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'start',
                      alignItems: 'center',
                      gap: '8px',
                      overflowX: 'auto',
                    }}
                  >
                    <Chip
                      size='small'
                      label={
                        <Typography variant='subtitle1'>Shared By:</Typography>
                      }
                    />
                    {/* <Typography variant='subtitle1'> Shared By: </Typography> */}
                    <Typography
                      variant='subtitle1'
                      sx={{ textAlign: 'start' }}
                    >
                      {item.customers
                        .map(
                          (customer) =>
                            // <Chip label={customer.name} />
                            customer.name,
                        )
                        .join(', ')}
                    </Typography>
                  </Box>
                </Button>
                <Dialog
                  fullWidth
                  open={modifyItemDialogOpen}
                >
                  <DialogContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: '16px',
                    }}
                  >
                    <Typography variant='h6'> Modify Item </Typography>
                    <TextField
                      label='Item Name'
                      fullWidth
                      autoFocus
                      value={modifiedItemInput.name}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const newModifiedItemInput = {
                          ...modifiedItemInput,
                        };
                        newModifiedItemInput.name = event.target.value;
                        setModifiedItemInput(newModifiedItemInput);
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <Typography
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        $
                      </Typography>
                      <TextField
                        label='Item Price'
                        fullWidth
                        autoFocus
                        value={modifiedItemInput.price}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const newModifiedItemInput = {
                            ...modifiedItemInput,
                          };
                          newModifiedItemInput.price = event.target.value;
                          setModifiedItemInput(newModifiedItemInput);
                        }}
                      />
                    </Box>
                    <Autocomplete
                      ListboxProps={{
                        style: {
                          maxHeight: '150px',
                        },
                      }}
                      fullWidth
                      multiple
                      value={getNonEmptyCustomers(modifiedItemInput.customers)}
                      onChange={(event, newCustomers) => {
                        let newModifiedItemInput = {
                          ...modifiedItemInput,
                          customers: newCustomers,
                        };
                        setModifiedItemInput(newModifiedItemInput);
                      }}
                      options={getNonEmptyCustomers(customers)}
                      disableCloseOnSelect
                      getOptionLabel={(customer) => customer.name}
                      // TODO: find a way to allow user to select all customers for an item.
                      //    it might have to be a checkbox on top of or below customers autocomplete textfield
                      renderOption={(props, customer, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon />}
                            checkedIcon={<CheckBoxIcon />}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {customer.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Customers'
                          placeholder='Customers'
                        />
                      )}
                    />
                  </DialogContent>
                  <DialogActions sx={{ padding: '16px 16px 24px 0' }}>
                    <Button
                      variant='contained'
                      color='secondary'
                      // sx={{ color: theme.palette.error.contrastText }}
                      onClick={() => {
                        setModifyItemDialogOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='contained'
                      color='primary'
                      autoFocus
                      onClick={() => {
                        const newItems = [
                          // NOTE: have to use the modifiedItemIndex instead of the normal index from the map bc for some reason, the index that is being used for this Button is the last index. idk why? maybe it has to do with something about keys?
                          ...items.slice(0, modifiedItemIndex),
                          {
                            ...modifiedItemInput,
                            name: modifiedItemInput.name.trim(),
                            price: modifiedItemInput.price.trim(),
                          },
                          ...items.slice(modifiedItemIndex + 1, items.length),
                        ];
                        // console.log(customerIndex);
                        setItems(newItems);
                        setModifiedItemInput({
                          name: '',
                          price: '',
                          customers: [],
                        });
                        setModifyItemDialogOpen(false);
                      }}
                    >
                      Save Changes
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid>
              {/* display delete icon */}
              <Grid
                item
                xs={1}
                sx={{ display: 'flex', alignItems: 'start' }}
              >
                <IconButton
                  onClick={() => {
                    setDeleteItemDialogOpen(true);
                    setModifiedItemIndex(index);
                  }}
                >
                  <DeleteIcon color='error' />
                </IconButton>
                <Dialog
                  fullWidth
                  open={deleteItemDialogOpen}
                >
                  <DialogTitle
                    sx={{ display: 'flex', justifyContent: 'center' }}
                  >
                    Delete Item?
                  </DialogTitle>
                  <DialogContent
                    sx={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <Typography
                      variant='body1'
                      sx={{
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        maxWidth: '10rem',
                      }}
                    >{`${items[modifiedItemIndex].name}`}</Typography>
                    <Typography variant='body1'>{`($${items[modifiedItemIndex].price})`}</Typography>
                  </DialogContent>
                  <DialogActions
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '0 24px 16px 24px',
                    }}
                  >
                    <Button
                      variant='contained'
                      color='secondary'
                      // sx={{ color: theme.palette.error.contrastText }}
                      onClick={() => {
                        setDeleteItemDialogOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='contained'
                      color='error'
                      autoFocus
                      onClick={() => {
                        // NOTE: using the modifiedItemIndex.bc for some reason, using the index variable from the map causes it to use the last index of the array. idk why
                        const newItems = [
                          ...items.slice(0, modifiedItemIndex),
                          ...items.slice(modifiedItemIndex + 1, items.length),
                        ];
                        setItems(newItems);
                        setDeleteItemDialogOpen(false);
                      }}
                    >
                      Delete
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid>
            </Grid>
          );
        })}
      </Box>
      {/* displays button to add items */}
      <Button
        fullWidth
        variant='contained'
        color='secondary'
        startIcon={<AddIcon />}
        onClick={() => {
          setAddItemDialogOpen(true);
        }}
      >
        Add Item
      </Button>
      <Dialog
        fullWidth
        open={addItemDialogOpen}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '16px',
          }}
        >
          <Typography variant='h6'> Add Item </Typography>
          <TextField
            label='Item Name'
            fullWidth
            autoFocus
            value={newItemInput.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const modifiedNewItemInput = {
                ...newItemInput,
              };
              modifiedNewItemInput.name = event.target.value;
              setNewItemInput(modifiedNewItemInput);
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              $
            </Typography>
            <TextField
              label='Item Price'
              fullWidth
              autoFocus
              value={newItemInput.price}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const modifiedNewItemInput = {
                  ...newItemInput,
                };
                modifiedNewItemInput.price = event.target.value;
                setNewItemInput(modifiedNewItemInput);
              }}
            />
          </Box>
          <Autocomplete
            ListboxProps={{
              style: {
                maxHeight: '150px',
              },
            }}
            fullWidth
            multiple
            value={getNonEmptyCustomers(newItemInput.customers)}
            onChange={(event, newCustomers) => {
              let modifiedNewItemInput = {
                ...newItemInput,
                customers: newCustomers,
              };
              setNewItemInput(modifiedNewItemInput);
            }}
            options={getNonEmptyCustomers(customers)}
            disableCloseOnSelect
            getOptionLabel={(customer) => customer.name}
            // TODO: find a way to allow user to select all customers for an item.
            //    it might have to be a checkbox on top of or below customers autocomplete textfield
            renderOption={(props, customer, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon />}
                  checkedIcon={<CheckBoxIcon />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {customer.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Customers'
                placeholder='Customers'
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 16px 24px 0' }}>
          <Button
            variant='contained'
            color='secondary'
            // sx={{ color: theme.palette.error.contrastText }}
            onClick={() => {
              setNewItemInput({ name: '', price: '', customers: [] });
              setAddItemDialogOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            autoFocus
            onClick={() => {
              const newItems = [
                ...items,
                {
                  ...newItemInput,
                  name: newItemInput.name.trim(),
                  price: newItemInput.price.trim(),
                },
              ];
              // console.log(customerIndex);
              setItems(newItems);
              setNewItemInput({ name: '', price: '', customers: [] });
              setAddItemDialogOpen(false);
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CollectItems;
